// The wireframes' shared states (SCR-002..017 style guide): loading skeleton, empty with its
// primary action, error banner with code, message and Retry, forbidden, not found.
import type { ComponentChildren } from "preact";
import type { ApiError } from "../api/errors";
import { errorDetailLines } from "./forms";

export function Loading({ label = "Loading…", rows = 3 }: { label?: string; rows?: number }) {
  return (
    <div class="state state-loading" role="status" aria-busy="true">
      <span class="visually-hidden">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} class="skeleton-row" aria-hidden="true" />
      ))}
    </div>
  );
}

export function Empty({ message, action }: { message: string; action?: ComponentChildren }) {
  return (
    <div class="state state-empty">
      <p>{message}</p>
      {action}
    </div>
  );
}

/** An API error's field-level details (e.g. a 422's `bucket: must be …`), or nothing. */
export function ErrorDetails({ error }: { error: ApiError }) {
  const lines = errorDetailLines(error);
  if (lines.length === 0) return null;
  return (
    <ul class="error-details">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

/** `requestId={false}` keeps the request id (a uuid) off a screen that must never show one (AS-1). */
export function ErrorBanner({ error, onRetry, details = true, requestId = true }: { error: ApiError; onRetry?: () => void; details?: boolean; requestId?: boolean }) {
  return (
    <div class="banner banner-error" role="alert">
      <p>
        <code class="error-code">{error.code}</code> {error.message}
      </p>
      {details && <ErrorDetails error={error} />}
      {requestId && error.requestId && <p class="request-id">Request id: <code>{error.requestId}</code></p>}
      {onRetry && (
        <button type="button" class="button" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function Forbidden({ what }: { what?: string }) {
  return (
    <div class="state state-forbidden">
      <h1>Not available to your role</h1>
      <p>{what ? `${what} is` : "This page is"} not available to your role. Ask a super-admin if you need it.</p>
    </div>
  );
}

export function NotFound() {
  return (
    <div class="state state-empty">
      <h1>Page not found</h1>
      <p>Nothing lives at this address. Use the navigation to find what you need.</p>
    </div>
  );
}
